import { useCallback, useEffect, useRef, useState } from 'react'
import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { Check, LineChart, PencilRuler, Rocket, SlidersHorizontal } from 'lucide-react'
import {
  AgenticToggle,
  AskAIPill,
  AskAISheet,
  Button,
  ChatHeader,
  EffortSelector,
  ModelPicker,
  Navbar,
  StrategyModeToggle,
  StrategyNavTabs,
  Workbench,
} from '@/components'
import { useContext } from 'react'
import { AppContext } from '../providers/AppProvider.jsx'
import { botApi } from './api'
import { CHAT_EFFORTS, effortDescription, effortLabel, isEffortAbove } from './chatEffort'
import {
  activeModel,
  agenticEnabled,
  effortValue,
  hasReasoning,
  resolveSelection,
  selectionPayload,
  selectionStorage,
} from './chatSelection'
import { backtestCoachStore } from './backtestCoach'
import { chartPush, useChartPush } from './chartPush'
import { workbenchPersist } from './workbenchPersist'
import { chartActivity, useComposerNudge, useHasDrawings } from './chartActivity'
import { toWorkbenchSummary, WORKBENCH_MESSAGE_MAX } from './workbenchUpdate'
import { walkthroughAutoplay } from './walkthroughAutoplay'
import { canvasSuspend } from './canvasSuspend'
import BacktestCoach from './components/BacktestCoach'
import BotChat from './components/BotChat'
import BotBacktest from './components/BotBacktest'
import BotOptimizer from './components/BotOptimizer'
import BotLive from './BotLive'
import { useSSE } from '../contexts/SSEContext'
import { SSE_EVENTS, MESSAGE_STATUS, REPLY_OFFER } from '../contexts/SSEEvents'
import { paywallTrigger } from '../system/paywallTrigger'
import { longRunNoticeStore } from './longRunNotice'
import ResizablePanel from '../components-legacy/ResizablePanel'

const PHONE_QUERY = '(max-width: 639px)'

const STRATEGY_NAV_TABS = [
  { id: 'workbench', label: 'Create', icon: PencilRuler },
  { id: 'backtest', label: 'Backtest', icon: LineChart },
  { id: 'optimizer', label: 'Optimise', icon: SlidersHorizontal },
]

// The "Run Backtest" quick action above the composer is hidden for now — flip
// back to re-enable it. Nothing behind it was removed: BotChat still renders it
// on the prop, and the send path it triggers (and its paid-plan gate) is shared
// with the optimizer badge, which is still live.
const SHOW_BACKTEST_QUICK_ACTION = false

const UPGRADE_MSG_ID = 'credits-exhausted'
const CREDITS_EXHAUSTED_MESSAGE =
  "You've used all of your credits. Visit billing to manage your subscription and credit usage."

// Distinct id from the credits card: running out of allowance and not being
// entitled to run at all are different problems, so the two cards dedupe
// independently rather than replacing each other.
const PAID_FEATURE_MSG_ID = 'paid-feature'
const PAID_FEATURE_MESSAGE =
  'Backtests and the optimizer are available on paid plans. Upgrade to run your strategy against real market data.'

// Said once per session, the moment the trader allows notifications on a long run.
const NOTIFICATIONS_MSG_ID = 'notifications-on'
const NOTIFICATIONS_MESSAGE =
  "This one will take a while. Your notifications are on, so you can switch strategies or even close this tab - it keeps running and I'll tell you the moment it's done."

export default function BotDetail() {
  const { bid } = useParams()
  const navigate = useNavigate()
  const outletContext = useOutletContext() || {}
  const { isMobile = false, drawerOpen = false, setDrawerOpen } = outletContext

  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  // No ?tab= param → the Create (workbench) tab is the landing tab.
  const topTab =
    tabParam === 'optimizer'
      ? 'optimizer'
      : tabParam === 'backtest'
        ? 'backtest'
        : 'workbench'
  const mode = searchParams.get('mode') === 'published' ? 'published' : 'builder'
  // Walkthrough is a MODE of the Create tab, kept in the URL alongside the other view params:
  // the Workbench unmounts when the user visits another tab, so local state would drop it, and
  // a shared/reloaded URL should land back in the same mode.
  const walkthrough = searchParams.get('wt') === '1'
  const chatParam = searchParams.get('chat')
  const chatHidden = chatParam === '0'
  const mobileChatMode =
    chatParam === 'sheet' || chatParam === 'expanded' ? chatParam : 'pill'

  const updateSearchParam = useCallback(
    (key, value) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value === null || value === undefined) next.delete(key)
          else next.set(key, value)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const [bot, setBot] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // Chat state. The draft text lives entirely inside the uncontrolled
  // <ChatInput> so typing never re-renders BotDetail or the heavy
  // main-column subtree (charts, etc.). The send handler receives the
  // text via its argument when the user submits.
  const [messages, setMessages] = useState([])
  const [isChatLoading, setIsChatLoading] = useState(false)

  // Reasoning effort for outgoing messages. The composer is uncontrolled, so
  // this lives here alongside the send handlers rather than inside ChatInput.
  // Read from storage lazily so a refresh keeps the last choice.
  const { session } = useContext(AppContext) ?? {}

  // Model and reasoning effort travel together as one stored preference.
  const [stored, setStored] = useState(() => selectionStorage.get())
  // What the server says is on offer, including which controls this plan locks.
  // Null until it answers; nothing renders a picker before then.
  const [modelsConfig, setModelsConfig] = useState(null)

  useEffect(() => {
    let cancelled = false
    botApi
      .getModels()
      .then((config) => {
        if (!cancelled) setModelsConfig(config)
      })
      // Not fatal: without it the composer just shows no picker and the server
      // applies its own default, which is the same as the picker being off.
      .catch((err) => console.error('Could not load chat models:', err))
    return () => {
      cancelled = true
    }
  }, [])

  // The single source of truth for what a send carries. Reconciles the stored
  // preference against what the server currently offers, so a retired model or a
  // plan downgrade can never leave the composer showing a choice the server
  // would silently override.
  const selection = resolveSelection(stored, modelsConfig)

  const updateSelection = useCallback((patch) => {
    setStored((prev) => {
      const next = { ...prev, ...patch }
      selectionStorage.set(next)
      return next
    })
  }, [])

  const setEffort = useCallback((next) => updateSelection({ effort: next }), [updateSelection])
  const setModel = useCallback((next) => updateSelection({ model: next }), [updateSelection])
  // Auto-verify: a real choice either way once touched, so both true and false are stored
  // (null = never touched = on). Rides to the server inside sendSelection like the others.
  const setAgentic = useCallback((next) => updateSelection({ agentic: next }), [updateSelection])

  // The most this plan may ask for; null means no ceiling. Taken from the server's
  // own answer rather than re-derived from the plan slug, so the two cannot drift —
  // which is the whole point, because the value moves: token-metered plans sat at
  // 'none' and now sit at 'medium'. Anything at or below this stays selectable,
  // anything above wears a lock.
  const maxEffort = modelsConfig?.maxEffort ?? null
  // Never null: the control always shows something selected, even before the
  // user has expressed a preference.
  const effectiveEffort = effortValue(selection, modelsConfig)
  const sendSelection = selectionPayload(selection)

  // Effort IS the reasoning control, so a model that cannot reason gets it
  // disabled rather than removed. 128 of OpenRouter's 414 models are in that
  // position, and a control that vanishes as you switch models explains nothing.
  const modelReasons = hasReasoning(selection, modelsConfig)

  // Which tiers the answering model actually accepts, straight from the server —
  // the per-model list lives in tbl_model because it genuinely varies: gpt-5.2,
  // 5.4 and 5.5 take none..xhigh, the 5.6 series adds max, and gpt-5-nano takes
  // minimal..high and cannot be switched off at all.
  //
  // Resolved via activeModel, NOT by looking selection.model up: with the picker
  // off that field is deliberately null, and matching on it fell through to the
  // full seven-tier list — offering efforts the pinned model rejects.
  const answeringModel = activeModel(selection, modelsConfig)
  // Only reachable before the server answers, which is why it is the full list
  // rather than empty: a momentarily over-wide menu beats a momentarily empty one.
  const modelEfforts = answeringModel?.efforts ?? CHAT_EFFORTS

  const effortOptions = modelEfforts.map((value) => ({
    value,
    label: effortLabel(value),
    description: effortDescription(value),
    // Locked only if it costs MORE than the plan allows. The old rule locked
    // everything except the exact pinned tier, which put an upgrade prompt on
    // options cheaper than the one the plan already permitted — a lock that
    // sells nothing. A free plan can still choose Disabled.
    locked: isEffortAbove(value, maxEffort),
  }))

  // Workbench changes the Workbench keeps staged for the composer's Send to AI.
  const push = useChartPush(bid)
  // The workbench update Send to AI has on the wire, until the server has stored
  // it. The request itself only resolves when the AI's whole turn is over, so the
  // commit happens on whichever comes first: the SSE event announcing the stored
  // row, or the response. Meanwhile the composer's tab stays down — the card is
  // already in the feed, and "Chart updated" beside it would be a lie.
  // Kept PER BOT (bid → the staged snapshot sent): the turn can outlive the visit —
  // the user may open another strategy long before it ends — and its commit must
  // land on the bot it was sent from, never hold back or reset the one on screen.
  // Takes the bid so the commit stays a stable callback (the SSE handler below is
  // memoised once).
  const workbenchInFlightRef = useRef(new Map())
  const [workbenchSendingBids, setWorkbenchSendingBids] = useState(() => new Set())
  const workbenchSending = workbenchSendingBids.has(bid)
  const settleWorkbenchInFlight = useCallback((sentBid, commit) => {
    const staged = workbenchInFlightRef.current.get(sentBid)
    if (!staged) return
    workbenchInFlightRef.current.delete(sentBid)
    if (commit) {
      chartPush.commit(sentBid, staged)
      // A mounted Workbench folds the commit into its diff base and saves it. With none
      // (sent from the Backtest or Optimise tab, or the user has moved on), it is persisted
      // here — a reload would otherwise offer the same changes again.
      if (!chartPush.isWorkbenchAttached(sentBid)) {
        workbenchPersist.persistCommit(sentBid).catch((err) => {
          if (import.meta.env.DEV) console.error('Persisting the Send to AI commit failed:', err)
        })
      }
    }
    setWorkbenchSendingBids((prev) => {
      const next = new Set(prev)
      next.delete(sentBid)
      return next
    })
  }, [])
  const commitWorkbenchInFlight = useCallback(
    (sentBid) => settleWorkbenchInFlight(sentBid, true),
    [settleWorkbenchInFlight],
  )
  // The bot on screen, for an async send that resolves after the user moved on.
  const bidRef = useRef(bid)
  useEffect(() => {
    bidRef.current = bid
  }, [bid])

  // One-shot: their first ever trade idea on this strategy just landed. The chat's
  // composer is already showing "Send chart to AI" for it; on a phone the chat is a
  // closed pill, so the sheet opens onto it (see the effect below).
  const composerNudge = useComposerNudge(bid)
  const wantsComposerNudge = composerNudge && mode === 'builder' && messages.length === 0

  // Is there anything on the canvas to walk through? Published by the Workbench, which unmounts
  // on the other tabs — hence the store. Decides which next step the chat may offer below.
  const hasDrawings = useHasDrawings(bid)

  // Whether this plan may run backtests / optimizer sweeps at all. Defaults to
  // allowed when the field is absent, so the bundle is safe to deploy before the
  // API starts sending it — the server is the enforcement point regardless.
  const canRunBacktest = session?.subscription?.canRunBacktest !== false

  const handleUpgradeClick = useCallback(
    (action) => (action === 'paywall' ? paywallTrigger.open() : navigate('/billing')),
    [navigate],
  )

  // Local-only pseudo-message: never persisted, and only ever one per id.
  const pushUpgradeMessage = useCallback((id, content, extra = {}) => {
    setMessages((prev) => [
      ...prev.filter((m) => m.id !== id),
      { id, role: 'upgrade', content, timestamp: new Date(), ...extra },
    ])
  }, [])

  useEffect(
    () =>
      longRunNoticeStore.subscribe(() => {
        const notice = longRunNoticeStore.get()
        if (!notice.enabled || notice.announced) return
        longRunNoticeStore.markAnnounced()
        setMessages((prev) => [
          ...prev.filter((m) => m.id !== NOTIFICATIONS_MSG_ID),
          {
            id: NOTIFICATIONS_MSG_ID,
            role: 'assistant',
            content: NOTIFICATIONS_MESSAGE,
            timestamp: new Date(),
            status: MESSAGE_STATUS.PROCESSED,
          },
        ])
      }),
    [],
  )

  const showCreditsExhaustedMessage = useCallback(() => {
    pushUpgradeMessage(UPGRADE_MSG_ID, CREDITS_EXHAUSTED_MESSAGE)
  }, [pushUpgradeMessage])

  const showPaidFeatureMessage = useCallback(() => {
    pushUpgradeMessage(PAID_FEATURE_MSG_ID, PAID_FEATURE_MESSAGE, {
      upgradeAction: 'paywall',
      ctaLabel: 'Pick a plan',
    })
  }, [pushUpgradeMessage])

  // Code state
  const [currentCode, setCurrentCode] = useState(null)
  const [hasCode, setHasCode] = useState(false)
  // Code the AI wrote during this visit, until the Workbench pane's Code tab is looked at: the
  // tab's muted dot (PROD-3133, as in the prototype). The code a page opens with is not news.
  const [unseenCodeCid, setUnseenCodeCid] = useState(null)

  // Lower panel tab state (Results / Equity / Trades / Logs / Code / Input / Entries / PnL / Results old)
  const [lowerTab, setLowerTab] = useState('results')

  // Refresh triggers for backtest and optimizer
  const [backtestRefreshTrigger, setBacktestRefreshTrigger] = useState(0)
  const [optimizerRefreshTrigger, setOptimizerRefreshTrigger] = useState(0)
  const queuedBacktestRefreshKeysRef = useRef(new Set())

  // Publish state
  const [isPublishing, setIsPublishing] = useState(false)
  const [publishRefreshTrigger, setPublishRefreshTrigger] = useState(0)
  const [deployPulseId, setDeployPulseId] = useState(null)
  const deployPulseTimerRef = useRef(null)

  useEffect(() => {
    return () => {
      if (deployPulseTimerRef.current) {
        clearTimeout(deployPulseTimerRef.current)
        deployPulseTimerRef.current = null
      }
    }
  }, [])

  // Phone detection (< 640) for icon-only deploy CTA
  const [isPhone, setIsPhone] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(PHONE_QUERY).matches : false,
  )

  useEffect(() => {
    const mq = window.matchMedia(PHONE_QUERY)
    const handler = (e) => setIsPhone(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Cross-breakpoint sanitization: ?chat=sheet|expanded is mobile-only,
  // ?chat=0 is desktop-only. Drop stale values when isMobile flips so the URL
  // never carries a state that doesn't match the current breakpoint.
  useEffect(() => {
    if (isMobile && chatParam === '0') {
      updateSearchParam('chat', null)
    } else if (!isMobile && (chatParam === 'sheet' || chatParam === 'expanded')) {
      updateSearchParam('chat', null)
    }
  }, [isMobile, chatParam, updateSearchParam])

  // A surface has covered the chart — the mobile chat sheet or the nav drawer — so
  // tell the Workbench to put the canvas down. Without it, an armed tool or a trade
  // idea in edit mode leaves its strip and coloured rim painted over that surface,
  // and every new overlay in the app would otherwise have to out-stack the whole
  // workbench chrome band. Derived from state rather than hung off the open handlers,
  // so a sheet arriving straight from the URL (a shared link) counts too.
  const chatCovering = isMobile && mobileChatMode !== 'pill'
  const covered = chatCovering || drawerOpen
  useEffect(() => {
    if (covered) canvasSuspend.request()
  }, [covered])

  // Their first ever trade idea just landed and the chat is a closed pill: open the
  // sheet so they SEE the "Send chart to AI" tab waiting for them.
  //
  // Spent the moment it is seen, on every layout, and deliberately not a
  // reconciliation of ?chat=. Two reasons it has to be an edge:
  //  - it must never re-open a sheet mid-close. BottomSheet only clears `isClosing`
  //    when `open` goes false, and during its 300ms exit ?chat= is still 'sheet', so
  //    re-asserting the param there would strand the panel off-screen under a live
  //    full-screen backdrop that has no pointer-events-none.
  //  - `updateSearchParam` changes identity on every URL search change (react-router
  //    memoises setSearchParams on location.search), so this effect re-runs on any
  //    URL edit. Clearing the one-shot is what makes those re-runs no-ops.
  useEffect(() => {
    if (!composerNudge) return
    // A collapsed desktop column stays collapsed — that was a deliberate choice, and
    // the sanitizer above owns ?chat=0. Never downgrade 'expanded', never touch the
    // param while a sheet is already up.
    if (isMobile && wantsComposerNudge && mobileChatMode === 'pill') {
      updateSearchParam('chat', 'sheet')
    }
    chartActivity.clearComposerNudge(bid)
  }, [composerNudge, isMobile, wantsComposerNudge, mobileChatMode, bid, updateSearchParam])

  useEffect(() => {

    if (!bid) {
      setError('Strategy ID is missing')
      setIsLoading(false)
      return
    }

    // Reset all state when bot changes
    setMessages([])
    setIsChatLoading(false)
    setCurrentCode(null)
    setHasCode(false)
    setUnseenCodeCid(null)
    queuedBacktestRefreshKeysRef.current.clear()
    backtestCoachStore.reset()
    chartPush.ensure(bid)
    chartActivity.ensure(bid)

    loadBot()
    initializeChat()
    loadCurrentCode()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bid])

  // Subscribe to code generation events
  useSSE(
    useCallback((msg) => msg.event === SSE_EVENTS.BOT_CODE_GENERATED && msg.botId === bid, [bid]),
    useCallback(
      (message) => {
        console.log('Code generated event received:', message)
        setHasCode(true)
        if (bid) {
          botApi
            .getCurrentCode(bid)
            .then((code) => {
              if (code) {
                setCurrentCode(code)
                setHasCode(true)
                setUnseenCodeCid(code.cid)
              }
            })
            .catch((err) => {
              console.error('Error loading code:', err)
            })
        }
        // No tab switch: the chat message announces the generated code; the
        // Code sub-tab reads currentCode whenever the user opens it.
      },
      // A state setter is stable; listed because the compiler infers it.
      [bid, setUnseenCodeCid],
    ),
  )

  // Subscribe to optimizer completion events
  useSSE(
    useCallback((msg) => msg.event === SSE_EVENTS.BOT_OPTIMIZER_COMPLETED && msg.botId === bid, [bid]),
    useCallback(
      (message) => {
        console.log('Optimizer completed event received:', message)
        setOptimizerRefreshTrigger((prev) => prev + 1)
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [bid],
    ),
  )

  // Subscribe to backtest completion events. No tab switch — the chat message
  // announces completion; refresh so an already-open Backtest tab shows the
  // final results, and a later manual visit refetches on mount anyway.
  useSSE(
    useCallback((msg) => msg.event === SSE_EVENTS.BOT_BACKTEST_COMPLETED && msg.botId === bid, [bid]),
    useCallback((message) => {
      console.log('Backtest completed event received:', message)
      setBacktestRefreshTrigger((prev) => prev + 1)
    }, []),
  )

  const loadBot = async () => {
    if (!bid) return

    setIsLoading(true)
    setError(null)

    try {
      const data = await botApi.get(bid)
      setBot(data)
    } catch (err) {
      console.error('Error loading bot:', err)
      setError('Failed to load strategy details')
    } finally {
      setIsLoading(false)
    }
  }

  const initializeChat = async () => {
    if (!bid) return

    try {
      const data = await botApi.getMessages(bid)

      const transformMessage = (msg) => {
        // Handle tool messages (new format with role: 'tool')
        if (msg.role === 'tool') {
          try {
            const parsed = JSON.parse(msg.content)

            // console.log('Loading tool message from database:', {
            //   msgId: msg.msgId,
            //   status: msg.status,
            //   toolName: parsed.name,
            //   toolStatus: parsed.status,
            // })

            return {
              id: msg.msgId.toString(),
              role: 'assistant',
              content: '',
              timestamp: new Date(parsed.startedAt || new Date()),
              status: msg.status,
              messageType: 'tool',
              toolData: {
                toolName: parsed.name,
                toolInput: parsed.arguments,
                callId: parsed.call_id,
                startedAt: parsed.startedAt,
                completedAt: parsed.completedAt,
                status: parsed.status,
                componentType: parsed.componentType,
                componentData: parsed.componentData,
                responses: parsed.responses,
                dismissed: parsed.dismissed,
                output: parsed.output,
                toolStatus: parsed.toolStatus,
                btid: parsed.btid,
                cid: parsed.cid,
                optid: parsed.optid,
                version: parsed.version,
                codeVersion: parsed.codeVersion,
                codeId: parsed.codeId,
                config: parsed.config,
                // run_walkthrough (Auto-verify): the round, the run's window and the verdict.
                round: parsed.round,
                maxRounds: parsed.maxRounds,
                autoRun: parsed.autoRun,
                walkthrough: parsed.walkthrough,
                matching: parsed.matching,
                assessment: parsed.assessment,
                verified: parsed.verified,
                error: parsed.error,
              },
            }
          } catch (err) {
            console.error('Error parsing tool message:', err)
            return {
              id: msg.msgId.toString(),
              role: 'assistant',
              content: '[Error loading tool message]',
              timestamp: new Date(),
              status: msg.status,
              messageType: 'content',
            }
          }
        }

        if (msg.role === 'assistant') {
          return {
            id: msg.msgId.toString(),
            role: 'assistant',
            content: msg.content,
            // Only present when the turn was run with thinking on. This is what
            // makes the block survive a reload.
            ...(msg.reasoning ? { reasoning: msg.reasoning } : {}),
            // The reply asked whether to generate the code — the button survives a reload.
            ...(msg.offer ? { offer: msg.offer } : {}),
            timestamp: new Date(),
            status: msg.status,
            messageType: 'content',
          }
        }

        return {
          id: msg.msgId.toString(),
          role: msg.role,
          content: msg.content,
          timestamp: new Date(),
          status: msg.status,
        }
      }

      // Rows with role 'chart_context' are LEGACY: the chart snapshot the old
      // "Chart context" tick box sent alongside the following user message.
      // Their content is ALREADY a parsed object ({ label, text }) — hold it,
      // skip the row, and surface it as a chip attachment on the next user
      // message. Rows with role 'workbench' are Send to AI's own turns (content
      // likewise pre-parsed) and render as their own card.
      const transformedMessages = []
      let pendingChartContext = null
      for (const msg of data.messages) {
        if (msg.role === 'workbench') {
          transformedMessages.push({
            id: msg.msgId.toString(),
            role: 'workbench',
            content: '',
            workbench: toWorkbenchSummary(msg.content),
            timestamp: new Date(),
            status: msg.status,
          })
          continue
        }
        if (msg.role === 'chart_context') {
          if (typeof msg.content === 'object' && msg.content?.label) {
            pendingChartContext = msg.content
          }
          continue
        }

        const transformed = transformMessage(msg)
        if (msg.role === 'user' && pendingChartContext) {
          transformed.attachments = [{ name: pendingChartContext.label, kind: 'chart' }]
          pendingChartContext = null
        }
        transformedMessages.push(transformed)
      }

      // Merge with any messages already added via SSE to avoid the race where
      // SSE events arrive before this HTTP response and get overwritten.
      setMessages((prev) => {
        if (prev.length === 0) return transformedMessages

        const httpIds = new Set(transformedMessages.map((m) => m.id))
        const sseOnly = prev.filter((m) => !httpIds.has(m.id))
        return [...transformedMessages, ...sseOnly]
      })

      const hasInProgress = transformedMessages.some(
        (m) => m.status === MESSAGE_STATUS.IN_PROGRESS,
      )
      if (hasInProgress) {
        setIsChatLoading(true)
      }
    } catch (err) {
      console.error('Error loading messages:', err)
    }
  }

  const loadCurrentCode = async () => {
    if (!bid) return

    try {
      const code = await botApi.getCurrentCode(bid)
      if (code) {
        setCurrentCode(code)
        setHasCode(true)
      }
    } catch (err) {
      console.error('Error loading code:', err)
    }
  }

  const handlePublishesEmpty = () => {
    setCurrentCode((prev) => (prev ? { ...prev, isPublished: false } : prev))
  }

  const handlePublishBot = async () => {
    if (!bid) return
    setIsPublishing(true)
    try {
      await botApi.publishBot(bid)
      setCurrentCode((prev) => (prev ? { ...prev, isPublished: true } : prev))
      setPublishRefreshTrigger((prev) => prev + 1)
      setDeployPulseId((prev) => (prev ?? 0) + 1)
      if (deployPulseTimerRef.current) {
        clearTimeout(deployPulseTimerRef.current)
      }
      deployPulseTimerRef.current = setTimeout(() => {
        setDeployPulseId(null)
        deployPulseTimerRef.current = null
      }, 1800)
    } catch (err) {
      const errorCode =
        err && typeof err === 'object' && 'errorCode' in err
          ? String(err.errorCode)
          : ''
      const message =
        errorCode === 'CODE_ALREADY_PUBLISHED'
          ? 'This strategy code has already been deployed.'
          : errorCode === 'CODE_NOT_FOUND'
            ? 'No code found for this strategy.'
            : 'Failed to deploy the strategy. Please try again.'
      alert(message)
    } finally {
      setIsPublishing(false)
    }
  }

  const handleSSEMessage = useCallback(
    (message) => {
      console.log(
        'SSE message received:',
        message.event,
        'botId:',
        message.botId,
        'msgId:',
        message.msgId,
        'status:',
        message.status,
      )

      if (message.event === SSE_EVENTS.BOT_MESSAGE_USER) {
        const userMessage = {
          id: message.msgId.toString(),
          role: 'user',
          content: message.content,
          timestamp: new Date(),
          status: MESSAGE_STATUS.PROCESSED,
        }
        setMessages((prev) => [...prev, userMessage])
      }

      // Send to AI's own turn — every tab sees the card, the sending one included,
      // and for the sending tab it is the moment the AI has the changes.
      if (message.event === SSE_EVENTS.BOT_MESSAGE_WORKBENCH) {
        commitWorkbenchInFlight(message.botId)
        const id = message.msgId.toString()
        setMessages((prev) =>
          prev.some((m) => m.id === id)
            ? prev
            : [
                ...prev,
                {
                  id,
                  role: 'workbench',
                  content: '',
                  workbench: toWorkbenchSummary(message.content),
                  timestamp: new Date(),
                  status: MESSAGE_STATUS.PROCESSED,
                },
              ],
        )
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_STARTED) {
        setIsChatLoading(true)
        setMessages((prev) => {
          if (prev.some((m) => m.id === message.msgId.toString())) {
            return prev
          }
          return [
            ...prev,
            {
              id: message.msgId.toString(),
              role: 'assistant',
              content: '',
              timestamp: new Date(),
              status: MESSAGE_STATUS.IN_PROGRESS,
            },
          ]
        })
      }

      // Reasoning arrives ahead of the content, so this has to upsert exactly
      // like BOT_MESSAGE_CHUNK does — for a thinking turn it is usually the
      // FIRST thing seen for that msgId.
      if (message.event === SSE_EVENTS.BOT_MESSAGE_REASONING) {
        setIsChatLoading(true)

        setMessages((prev) => {
          const messageId = message.msgId.toString()
          const existingIndex = prev.findIndex((m) => m.id === messageId)

          if (existingIndex >= 0) {
            const updated = [...prev]
            updated[existingIndex] = {
              ...updated[existingIndex],
              reasoning: message.accumulated,
              streaming: true,
              status: MESSAGE_STATUS.IN_PROGRESS,
            }
            return updated
          }
          return [
            ...prev,
            {
              id: messageId,
              role: 'assistant',
              content: '',
              reasoning: message.accumulated,
              timestamp: new Date(),
              streaming: true,
              status: MESSAGE_STATUS.IN_PROGRESS,
            },
          ]
        })
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_CHUNK) {
        setIsChatLoading(true)

        setMessages((prev) => {
          const messageId = message.msgId.toString()
          const existingIndex = prev.findIndex((m) => m.id === messageId)

          if (existingIndex >= 0) {
            const updated = [...prev]
            updated[existingIndex] = {
              ...updated[existingIndex],
              content: message.accumulated,
              streaming: true,
              status: MESSAGE_STATUS.IN_PROGRESS,
            }
            return updated
          } else {
            return [
              ...prev,
              {
                id: messageId,
                role: 'assistant',
                content: message.accumulated,
                timestamp: new Date(),
                streaming: true,
                status: MESSAGE_STATUS.IN_PROGRESS,
              },
            ]
          }
        })
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_DONE) {
        console.log('Received bot-message-done, clearing loading state', {
          msgId: message.msgId,
          status: message.status,
          messageType: message.messageType,
        })
        setIsChatLoading(false)

        setMessages((prev) => {
          return prev.map((msg) => {
            if (msg.id === message.msgId.toString()) {
              if (message.messageType === 'tool') {
                return {
                  ...msg,
                  streaming: false,
                  status: MESSAGE_STATUS.PROCESSED,
                  messageType: 'tool',
                  toolData: msg.toolData
                    ? {
                        ...msg.toolData,
                        status: 'completed',
                        completedAt: message.completedAt || msg.toolData.completedAt,
                        ...(message.toolStatus && { toolStatus: message.toolStatus }),
                        ...(message.dismissed && { dismissed: true }),
                        ...(message.responses && { responses: message.responses }),
                        ...(message.btid && { btid: message.btid }),
                        ...(message.cid && { cid: message.cid }),
                        ...(message.optid && { optid: message.optid }),
                        ...(message.version != null && { version: message.version }),
                        ...(message.codeVersion != null && { codeVersion: message.codeVersion }),
                        ...(message.config && { config: message.config }),
                        ...(message.round != null && { round: message.round }),
                        ...(message.maxRounds != null && { maxRounds: message.maxRounds }),
                        ...(message.autoRun != null && { autoRun: message.autoRun }),
                        ...(message.walkthrough && { walkthrough: message.walkthrough }),
                        ...(message.matching && { matching: message.matching }),
                        ...(message.assessment && { assessment: message.assessment }),
                        ...(message.error && { error: message.error }),
                      }
                    : undefined,
                }
              }
              return {
                ...msg,
                content: message.content,
                // Assigned even when absent, so a retried reply cannot keep an old offer.
                offer: message.offer,
                streaming: false,
                status: MESSAGE_STATUS.PROCESSED,
                messageType: 'content',
              }
            }
            return msg
          })
        })

        if (
          message.toolStatus === 'BACKTEST_ERROR' ||
          message.toolStatus === 'BACKTEST_CANCELED'
        ) {
          setBacktestRefreshTrigger((prev) => prev + 1)
        }

        // First-ever completed backtest starts the onboarding coach, anchored
        // to this message's results badge (no-op once seen or while running).
        if (message.toolStatus === 'BACKTEST_COMPLETED' && message.btid) {
          backtestCoachStore.start(message.msgId.toString())
        }
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_DELETED) {
        console.log('Received bot-message-deleted', { msgId: message.msgId })
        setIsChatLoading(false)
        setMessages((prev) => prev.filter((msg) => msg.id !== message.msgId.toString()))
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_FAILED) {
        console.log('Received bot-message-failed', {
          msgId: message.msgId,
          error: message.error,
        })
        setIsChatLoading(false)
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === message.msgId.toString()
              ? { ...msg, streaming: false, status: MESSAGE_STATUS.FAILED, error: message.error }
              : msg,
          ),
        )
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_CANCELED) {
        console.log('Received bot-message-canceled', { msgId: message.msgId })
        setIsChatLoading(false)
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === message.msgId.toString()
              ? { ...msg, streaming: false, status: MESSAGE_STATUS.CANCELED }
              : msg,
          ),
        )
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_TYPE_CHANGED) {
        console.log('Received bot-message-type-changed', {
          msgId: message.msgId,
          messageType: message.messageType,
          toolName: message.toolName,
        })

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === message.msgId.toString()
              ? {
                  ...msg,
                  messageType: 'tool',
                  streaming: false,
                  toolData: {
                    toolName: message.toolName,
                    toolInput: message.toolInput || {},
                    startedAt: message.startedAt,
                    status: 'running',
                    componentType: message.componentType,
                    componentData: message.componentData,
                    toolStatus: message.toolStatus,
                  },
                }
              : msg,
          ),
        )
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_TOOL_STARTED) {
        console.log('Received bot-message-tool-started', {
          msgId: message.msgId,
          toolName: message.toolName,
          callId: message.callId,
          toolInput: message.toolInput,
        })

        setIsChatLoading(true)
        setMessages((prev) => {
          if (prev.some((m) => m.id === message.msgId.toString())) {
            return prev
          }
          return [
            ...prev,
            {
              id: message.msgId.toString(),
              role: 'assistant',
              content: '',
              timestamp: new Date(message.startedAt || new Date()),
              status: MESSAGE_STATUS.IN_PROGRESS,
              messageType: 'tool',
              toolData: {
                toolName: message.toolName,
                toolInput: message.toolInput || {},
                callId: message.callId,
                startedAt: message.startedAt || new Date().toISOString(),
                status: 'running',
              },
            },
          ]
        })
      }

      if (message.event === SSE_EVENTS.BOT_MESSAGE_TOOL_DONE) {
        console.log('Received bot-message-tool-done', {
          msgId: message.msgId,
          toolName: message.toolName,
          toolCallStatus: message.toolCallStatus,
        })

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === message.msgId.toString() && msg.toolData) {
              return {
                ...msg,
                status: MESSAGE_STATUS.PROCESSED,
                toolData: {
                  ...msg.toolData,
                  // The real outcome the server persisted. Assuming 'completed'
                  // here made a failed run look done until the page reloaded.
                  status: message.toolCallStatus || 'completed',
                  ...(message.cid && { cid: message.cid }),
                  ...(message.version != null && { version: message.version }),
                },
              }
            }
            return msg
          }),
        )
      }

      if (message.event === SSE_EVENTS.BOT_BACKTEST_STATUS) {
        console.log('Received bot-backtest-status', {
          msgId: message.msgId,
          toolStatus: message.toolStatus,
          backtestStatus: message.backtestStatus,
        })

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === message.msgId?.toString()) {
              return {
                ...msg,
                toolData: {
                  ...msg.toolData,
                  toolStatus: message.toolStatus,
                  config: message.config || msg.toolData?.config,
                  btid: message.btid || msg.toolData?.btid,
                  cid: message.cid || msg.toolData?.cid,
                  version: message.version ?? msg.toolData?.version,
                  codeVersion: message.codeVersion ?? msg.toolData?.codeVersion,
                  error: message.error || msg.toolData?.error,
                },
              }
            }
            return msg
          }),
        )

        if (message.toolStatus === 'BACKTEST_QUEUED') {
          setBacktestRefreshTrigger((prev) => prev + 1)
        }

        if (message.toolStatus === 'BACKTEST_RUNNING') {
          const refreshKey =
            message.msgId != null
              ? `msg:${message.msgId}`
              : message.btid
                ? `btid:${message.btid}`
                : null

          if (refreshKey && !queuedBacktestRefreshKeysRef.current.has(refreshKey)) {
            queuedBacktestRefreshKeysRef.current.add(refreshKey)
            setBacktestRefreshTrigger((prev) => prev + 1)
          }
        }
      }

      // The Auto-verify walkthrough card's stages. Same shape as the backtest status
      // handler: merge whatever the server has learned about the run so far.
      if (message.event === SSE_EVENTS.BOT_WALKTHROUGH_STATUS) {
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === message.msgId?.toString()) {
              return {
                ...msg,
                toolData: {
                  ...msg.toolData,
                  toolStatus: message.toolStatus,
                  round: message.round ?? msg.toolData?.round,
                  maxRounds: message.maxRounds ?? msg.toolData?.maxRounds,
                  autoRun: message.autoRun ?? msg.toolData?.autoRun,
                  btid: message.btid || msg.toolData?.btid,
                  cid: message.cid || msg.toolData?.cid,
                  version: message.version ?? msg.toolData?.version,
                  codeVersion: message.codeVersion ?? msg.toolData?.codeVersion,
                  walkthrough: message.walkthrough || msg.toolData?.walkthrough,
                  error: message.error || msg.toolData?.error,
                },
              }
            }
            return msg
          }),
        )
      }

      if (message.event === SSE_EVENTS.BOT_OPTIMIZER_STATUS) {
        console.log('Received bot-optimizer-status', {
          msgId: message.msgId,
          toolStatus: message.toolStatus,
          optimizerStatus: message.optimizerStatus,
        })

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === message.msgId?.toString()) {
              return {
                ...msg,
                toolData: {
                  ...msg.toolData,
                  toolStatus: message.toolStatus,
                  config: message.config || msg.toolData?.config,
                  optid: message.optid || msg.toolData?.optid,
                  version: message.version ?? msg.toolData?.version,
                  codeVersion: message.codeVersion ?? msg.toolData?.codeVersion,
                  error: message.error || msg.toolData?.error,
                },
              }
            }
            return msg
          }),
        )

        if (
          message.toolStatus === 'OPTIMIZER_QUEUED' ||
          message.toolStatus === 'OPTIMIZER_RUNNING'
        ) {
          setOptimizerRefreshTrigger((prev) => prev + 1)
        }
      }
    },
    [commitWorkbenchInFlight],
  )

  // Subscribe to SSE messages for this bot
  useSSE(
    useCallback((msg) => msg.botId === bid, [bid]),
    handleSSEMessage,
  )

  // Subscribe to credits-exhausted SSE event (fired from fire-and-forget startFirstMessage)
  useSSE(
    useCallback(
      (msg) => msg.event === SSE_EVENTS.BOT_CREDITS_EXHAUSTED && msg.botId === bid,
      [bid],
    ),
    useCallback(() => {
      setIsChatLoading(false)
      showCreditsExhaustedMessage()
    }, [showCreditsExhaustedMessage]),
  )

  // A run the AI attempted was refused by the plan's entitlement. Distinct from
  // credits-exhausted: nothing was consumed, the plan simply doesn't include it.
  useSSE(
    useCallback(
      (msg) => msg.event === SSE_EVENTS.BOT_UPGRADE_REQUIRED && msg.botId === bid,
      [bid],
    ),
    useCallback(() => showPaidFeatureMessage(), [showPaidFeatureMessage]),
  )

  const handleSendMessage = async (prompt, { intent } = {}) => {
    const text = (prompt ?? '').trim()
    if (!text || isChatLoading || !bid) return

    // A send that exists only to start a run is refused here on a plan that
    // can't run them, so the user doesn't spend a chat turn to be told no.
    // Free-text ("backtest this") has no intent and still goes to the server,
    // which is where the rule is actually enforced.
    if (intent && !canRunBacktest) {
      showPaidFeatureMessage()
      return
    }

    setIsChatLoading(true)

    try {
      await botApi.sendMessage(bid, text, sendSelection)
    } catch (err) {
      setIsChatLoading(false)
      if (err?.errorCode === 'INSUFFICIENT_CREDITS') {
        showCreditsExhaustedMessage()
      } else {
        console.error('Error sending message:', err)
      }
    }
  }

  // The composer's Send to AI: the staged workbench changes, as a turn of their
  // own, with whatever the user typed to go with them. Committed only once the
  // server has them — a send refused up front keeps them staged — and the
  // snapshot committed is the one sent, not whatever an edit made meanwhile has
  // staged since.
  //
  // Resolves false when nothing was sent, so the composer gives the typed text back.
  const handleSendWorkbench = async (typed = '') => {
    const sentBid = bid
    const staged = sentBid ? chartPush.get(sentBid).staged : null
    if (!staged || isChatLoading || workbenchInFlightRef.current.has(sentBid)) return false
    const message = typed.trim()
    // The server refuses a longer one (WORKBENCH_UPDATE_INVALID); the composer blocks it too.
    if (message.length > WORKBENCH_MESSAGE_MAX) return false

    workbenchInFlightRef.current.set(sentBid, staged)
    setWorkbenchSendingBids((prev) => new Set(prev).add(sentBid))
    setIsChatLoading(true)

    try {
      await botApi.sendWorkbenchUpdate(
        sentBid,
        {
          label: staged.label,
          text: staged.text,
          first: staged.first,
          counts: staged.counts,
          changes: staged.changes,
          omitted: staged.omitted,
          ...(message && { message }),
        },
        sendSelection,
      )
      commitWorkbenchInFlight(sentBid)
      return true
    } catch (err) {
      // Refused before the row was stored (nothing committed yet): offer the
      // changes again. A failure after it was stored already committed on the
      // SSE event — the turn then shows as failed, with Retry.
      const stored = !workbenchInFlightRef.current.has(sentBid)
      settleWorkbenchInFlight(sentBid, false)
      // The chat on screen may be another strategy's by now: leave it alone.
      if (bidRef.current !== sentBid) return stored
      setIsChatLoading(false)
      if (err?.errorCode === 'INSUFFICIENT_CREDITS') {
        showCreditsExhaustedMessage()
      } else {
        console.error('Error sending workbench update:', err)
      }
      return stored
    }
  }

  // Badge actions on a completed backtest's results card.
  const handleRunOptimizer = () =>
    handleSendMessage('run the parameter optimizer', { intent: 'optimizer' })

  const handleViewReport = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('tab', 'backtest')
        // On mobile the chat sheet covers the tabs — close it too.
        if (isMobile) next.delete('chat')
        return next
      },
      { replace: true },
    )
  }, [setSearchParams, isMobile])

  // Badge actions on the next-step prompt under a just-generated strategy.
  const handleRunWalkthrough = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        // The walkthrough lives on the Create tab's canvas, so that has to be the visible tab.
        next.delete('tab')
        next.set('wt', '1')
        // On mobile the chat sheet covers the chart the walkthrough plays on.
        if (isMobile) next.delete('chat')
        return next
      },
      { replace: true },
    )
    // Nothing more to do: the Workbench takes ?wt=1 as a one-shot request — it runs (or
    // re-attaches to) the walkthrough over the drawings as they are, shows it, and drops the
    // param. Deliberately NOT plan-gated, matching the Workbench's own Walkthrough toggle —
    // the band surfaces the billing error with a "Manage billing" action.
  }, [setSearchParams, isMobile])

  // A walkthrough is ready to watch — the verify loop accepted it, or the user asked for
  // it: mark the card when the verdict passed, and show it on the canvas so the user sees
  // the replay without asking for it. Only in builder mode (published has no workbench).
  // Also when one is already on the canvas: this is a NEWER run, and the Workbench attaches to the
  // run the server just built, since code, symbol, timeframe and period all match.
  useSSE(
    useCallback(
      (msg) => msg.event === SSE_EVENTS.BOT_WALKTHROUGH_READY && msg.botId === bid,
      [bid],
    ),
    useCallback(
      (message) => {
        if (message.verified === true) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === message.msgId?.toString() && msg.toolData
                ? { ...msg, toolData: { ...msg.toolData, verified: true } }
                : msg,
            ),
          )
        }
        if (mode !== 'builder') return
        // A passed verdict opens the replay PLAYING: the user waited for this answer in the chat,
        // and the answer is watching the code trade their drawings. Anything else opens paused.
        if (message.verified === true) walkthroughAutoplay.request(bid)
        handleRunWalkthrough()
      },
      [mode, handleRunWalkthrough, bid],
    ),
  )

  const handleRunBacktest = () =>
    handleSendMessage('run a backtest', { intent: 'backtest' })

  // Says yes to the AI's offer to write the code, exactly as typing it would: a plain message,
  // shown as the user's own, which is also what ends the offer. With chart changes waiting
  // ("Chart updated" on the composer) typing it would go out WITH them via Send to AI — and so
  // does the button: code generated without them would miss what the user just drew.
  const handleGenerateCode = () => {
    const answer = hasCode ? 'Yes, regenerate the code' : 'Yes, generate the code'
    if (mode === 'builder' && push.staged && !workbenchSending) return handleSendWorkbench(answer)
    return handleSendMessage(answer)
  }

  const handleViewCode = useCallback(() => {
    setLowerTab('code')
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('tab', 'backtest')
        if (isMobile) next.delete('chat')
        return next
      },
      { replace: true },
    )
    // setLowerTab is a stable setter; it is listed because the React Compiler infers it
    // as a dependency and refuses to preserve the memoization otherwise.
  }, [setSearchParams, isMobile, setLowerTab])

  const handleCancelMessage = async () => {
    if (!bid) return

    console.log('Canceling message for bot:', bid)

    try {
      await botApi.cancelMessage(bid)
    } catch (err) {
      console.error('Error canceling message:', err)
    }
  }

  const handleRetryMessage = async () => {
    if (!bid) return

    console.log('Retrying message for bot:', bid)
    setIsChatLoading(true)

    try {
      await botApi.retryMessage(bid, sendSelection)
    } catch (err) {
      setIsChatLoading(false)
      if (err?.errorCode === 'INSUFFICIENT_CREDITS') {
        showCreditsExhaustedMessage()
      } else {
        console.error('Error retrying message:', err)
      }
    }
  }

  const handleComponentSubmit = async (msgId, answers) => {
    if (!bid) return

    console.log('Component submitted:', msgId, answers)

    const msgIdNum = parseInt(msgId)

    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id === msgId && msg.toolData) {
          return {
            ...msg,
            status: MESSAGE_STATUS.PROCESSED,
            toolData: {
              ...msg.toolData,
              responses: answers,
              status: 'completed',
            },
          }
        }
        return msg
      }),
    )

    setIsChatLoading(true)
    try {
      await botApi.submitFormResponse(bid, msgIdNum, answers, sendSelection)
    } catch (err) {
      setIsChatLoading(false)
      if (err?.errorCode === 'INSUFFICIENT_CREDITS') {
        showCreditsExhaustedMessage()
      } else {
        console.error('Error sending component response:', err)
        // Reconcile the optimistic update: another tab may have already
        // submitted or dismissed this form (MESSAGE_ALREADY_PROCESSED).
        initializeChat()
      }
    }
  }

  const handleComponentDismiss = async (msgId) => {
    if (!bid) return

    console.log('Component dismissed:', msgId)

    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id === msgId && msg.toolData) {
          return {
            ...msg,
            status: MESSAGE_STATUS.PROCESSED,
            toolData: {
              ...msg.toolData,
              status: 'completed',
              dismissed: true,
              responses: {},
            },
          }
        }
        return msg
      }),
    )

    // Dismissing triggers no AI continuation — nothing is running anymore.
    setIsChatLoading(false)

    try {
      await botApi.dismissForm(bid, parseInt(msgId))
    } catch (err) {
      console.error('Error dismissing questions:', err)
      // Reconcile the optimistic update with the authoritative state.
      initializeChat()
    }
  }

  const handleMenuClick = useCallback(() => {
    if (typeof setDrawerOpen === 'function') setDrawerOpen((open) => !open)
  }, [setDrawerOpen])

  const showChat = !chatHidden && !isMobile

  const handleShowChat = useCallback(() => {
    if (isMobile) {
      updateSearchParam('chat', 'sheet')
    } else {
      updateSearchParam('chat', null)
    }
  }, [isMobile, updateSearchParam])

  const handleCollapseChat = useCallback(() => {
    if (isMobile) {
      updateSearchParam('chat', null)
    } else {
      updateSearchParam('chat', '0')
    }
  }, [isMobile, updateSearchParam])

  const deployVersionLabel = currentCode?.version != null ? `v${currentCode.version}` : null

  const deployCtaSlot = (() => {
    if (!hasCode || !currentCode) return null
    if (currentCode.isPublished) {
      return (
        <span
          aria-label={
            deployVersionLabel ? `Deployed ${deployVersionLabel}` : 'Deployed'
          }
          className="inline-flex items-center gap-1.5 h-7 px-3 rounded-md bg-muted text-caption text-muted-foreground select-none"
        >
          <Check size={11} strokeWidth={2.25} className="text-foreground" />
          {!isPhone && 'Deployed'}
          {deployVersionLabel && (
            <span className="font-mono tabular-nums opacity-70">
              {deployVersionLabel}
            </span>
          )}
        </span>
      )
    }
    return (
      <Button
        type="button"
        size="sm"
        onClick={isPublishing ? undefined : handlePublishBot}
        disabled={isPublishing}
        className="h-7 min-h-7 gap-1.5 text-caption px-3"
        title={
          isPhone
            ? deployVersionLabel
              ? `Deploy ${deployVersionLabel}`
              : 'Deploy'
            : undefined
        }
      >
        <Rocket size={10} strokeWidth={2} />
        {!isPhone && 'Deploy'}
        {deployVersionLabel && (
          <span className="font-mono tabular-nums opacity-70">
            {deployVersionLabel}
          </span>
        )}
      </Button>
    )
  })()

  if (isLoading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-body text-muted-foreground">
        Loading...
      </div>
    )
  }

  if (error) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-body text-danger">
        {error}
      </div>
    )
  }

  if (!bot) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-body text-muted-foreground">
        Strategy not found
      </div>
    )
  }

  /**
   * The next step offered under the conversation, as buttons rather than prose.
   *
   * Under a just-generated strategy: the AI no longer proposes one itself (see ToolHandlerCode.js),
   * because a walkthrough has no tool for it to call and only the client knows whether the chart
   * qualifies for one. The server writes the code-gen tool message and then a separate assistant
   * message for the summary, so "code was just generated and the AI has finished talking about
   * it" is exactly [tool, assistant] at the end.
   *
   * Under the AI asking "Want me to generate the code?": the reply carries `offer` (the server
   * takes it from a marker the model adds, see ReplyOffer.js), and the button says yes for the
   * user. The AI's question stays in its reply; typing an answer works just as well.
   *
   * Derived from the TAIL of the conversation rather than stored: being derived is what makes it
   * self-clearing — anything appended afterwards (their next message, a chained auto-backtest
   * card, an upgrade card) ends the offer — and it survives a reload for free, since the messages
   * are persisted.
   */
  const codeCta = (() => {
    const last = messages[messages.length - 1]
    const isFinishedReply =
      !!last &&
      last.role === 'assistant' &&
      last.messageType !== 'tool' &&
      !last.toolData &&
      last.status === MESSAGE_STATUS.PROCESSED &&
      !last.streaming
    if (!isFinishedReply) return null

    // The AI's offer is answered in any mode: the button only sends a chat message.
    const codeOffer =
      last.offer === REPLY_OFFER.GENERATE_CODE ? (hasCode ? 'regenerateCode' : 'generateCode') : null

    // Published mode has no workbench and no tab nav, so neither destination exists there.
    if (mode !== 'builder') return codeOffer
    // Both destinations need code on the strategy, and both are what the walkthrough replays.
    if (!hasCode) return codeOffer
    if (messages.length < 2) return codeOffer

    const prev = messages[messages.length - 2]

    // A walkthrough just finished and the AI has spoken about it — accepted it, or the user
    // asked for it: offer to WATCH it rather than to start one. Gated on drawings like the
    // walkthrough offer — the mode bounces without them.
    const isReadyWalkthrough =
      prev.toolData?.toolName === 'run_walkthrough' &&
      prev.toolData.status === 'completed' &&
      !!prev.toolData.btid
    if (isReadyWalkthrough) return hasDrawings ? 'walkthroughReady' : codeOffer

    const isCodeGen =
      prev.toolData?.toolName === 'generate_trading_code' &&
      prev.toolData.status === 'completed'
    if (!isCodeGen) return codeOffer

    // A walkthrough replays the period the user drew on, so a bare chart can only be backtested.
    return hasDrawings ? 'walkthrough' : 'backtest'
  })()

  const chatProps = {
    messages,
    onSendMessage: handleSendMessage,
    onComponentSubmit: handleComponentSubmit,
    onComponentDismiss: handleComponentDismiss,
    onCancelMessage: handleCancelMessage,
    onRetryMessage: handleRetryMessage,
    onUpgradeClick: handleUpgradeClick,
    onRunOptimizer: handleRunOptimizer,
    onViewReport: handleViewReport,
    codeCta,
    onRunWalkthrough: handleRunWalkthrough,
    // Same destination as the offer — the walkthrough for the CURRENT code, on the canvas.
    onViewWalkthrough: handleRunWalkthrough,
    onRunBacktest: handleRunBacktest,
    onGenerateCode: handleGenerateCode,
    onViewCode: handleViewCode,
    isLoading: isChatLoading,
    hasCode,
    // The backtest quick action would show on every builder tab (published
    // mode has no tab nav at all) — currently hidden, see the flag.
    showBacktestAction: SHOW_BACKTEST_QUICK_ACTION && mode === 'builder',
    // Published mode has no workbench, so there is nothing to send from it there.
    workbench: {
      pending:
        mode === 'builder' && push.staged && !workbenchSending
          ? {
              label: push.staged.label,
              first: push.staged.first,
              counts: push.staged.counts,
              changes: push.staged.changes,
              omitted: push.staged.omitted,
            }
          : null,
      onSend: handleSendWorkbench,
    },
    // The model picker and the reasoning effort share one chrome row with the
    // send button, so on a phone the model button drops its label — two labelled
    // controls plus Send do not fit at 360px.
    effortSlot: (
      <>
        {modelsConfig?.pickerEnabled && (
          <ModelPicker
            value={selection.model}
            options={(modelsConfig.models ?? []).map((model) => ({
              identifier: model.identifier,
              label: model.label,
            }))}
            onChange={setModel}
            compact={isPhone}
          />
        )}
        <EffortSelector
          value={effectiveEffort}
          options={effortOptions}
          onChange={setEffort}
          onLockedSelect={() => handleUpgradeClick('paywall')}
          disabled={!modelReasons}
        />
      </>
    ),
    // The Auto-verify switch, far left of the same chrome row. Shown in every mode: it is a
    // preference about how the assistant works, and the server ignores it where it cannot
    // apply (no drawings, published mode).
    chromeStartSlot: (
      <AgenticToggle checked={agenticEnabled(selection)} onCheckedChange={setAgentic} />
    ),
  }

  const renderMainContent = () => {
    if (mode === 'published') {
      return (
        <BotLive
          refreshTrigger={publishRefreshTrigger}
          onEmpty={handlePublishesEmpty}
        />
      )
    }
    if (topTab === 'workbench') {
      // Keyed by bid: navigating to another strategy must remount the workbench
      // so its persisted chart state restores cleanly and never crosses bots.
      return (
        <Workbench
          key={bot.bid}
          bid={bot.bid}
          walkthrough={walkthrough}
          onWalkthroughChange={(on) => updateSearchParam('wt', on ? '1' : null)}
          hasCode={hasCode}
          // The walkthrough validates one code version; currentCode is already refreshed by
          // the BOT_CODE_GENERATED handler, so a regeneration invalidates the run for free.
          codeId={currentCode?.codeId ?? null}
          // The same object the Backtest tab shows, for the walkthrough pane's Code tab.
          currentCode={currentCode}
          codeUnseen={unseenCodeCid != null && unseenCodeCid === currentCode?.cid}
          onCodeSeen={() => setUnseenCodeCid(null)}
          onManageBilling={() => navigate('/billing')}
        />
      )
    }
    if (topTab === 'optimizer') {
      return <BotOptimizer bid={bot.bid} refreshTrigger={optimizerRefreshTrigger} />
    }
    return (
      <BotBacktest
        bid={bot.bid}
        refreshTrigger={backtestRefreshTrigger}
        currentCode={currentCode}
        hasCode={hasCode}
        activeBottomTab={lowerTab}
        onActiveBottomTabChange={setLowerTab}
      />
    )
  }

  const tabsSlot =
    mode === 'builder' ? (
      <StrategyNavTabs
        tabs={STRATEGY_NAV_TABS}
        activeId={topTab}
        onTabChange={(id) => updateSearchParam('tab', id === 'workbench' ? null : id)}
        isMobile={isMobile}
      />
    ) : null

  const modeToggleSlot = (
    <StrategyModeToggle
      mode={mode}
      onModeChange={(next) => updateSearchParam('mode', next === 'builder' ? null : next)}
      deployPulseId={deployPulseId}
    />
  )

  const renderMainColumn = ({ showChatButton } = {}) => (
    <div className="absolute inset-0 flex flex-col overflow-hidden bg-card">
      <Navbar
        isMobile={isMobile}
        isPhone={isPhone}
        onMenuClick={isMobile ? handleMenuClick : undefined}
        showBorder
        tabsSlot={tabsSlot}
        modeToggleSlot={modeToggleSlot}
        showChatButton={!!showChatButton}
        onShowChat={handleShowChat}
      />
      <main className="flex-1 min-h-0 relative overflow-hidden">
        {renderMainContent()}
      </main>
    </div>
  )

  const renderChatColumn = ({
    onCollapse,
    isMobileLayout,
    hideHeader = false,
    inputTrailingSlot,
  }) => (
    <div className="absolute inset-0 flex flex-col overflow-hidden bg-background">
      {!hideHeader && (
        <ChatHeader
          title="Chat"
          onCollapse={onCollapse}
          deployCtaSlot={deployCtaSlot}
        />
      )}
      <div className="flex-1 min-h-0 relative">
        <BotChat
          {...chatProps}
          hideHeader
          isMobileLayout={isMobileLayout}
          inputTrailingSlot={inputTrailingSlot}
        />
      </div>
    </div>
  )

  return (
    <div className="absolute inset-0 overflow-hidden bg-card">
      <BacktestCoach
        isMobile={isMobile}
        onCloseMobileChat={() => updateSearchParam('chat', null)}
      />
      {isMobile ? (
        renderMainColumn()
      ) : showChat ? (
        <ResizablePanel
          defaultLeftWidth={60}
          minLeftWidth={30}
          maxLeftWidth={80}
          storageKey={`bot-detail-panel-${bid}`}
        >
          {renderMainColumn()}
          {renderChatColumn({ onCollapse: handleCollapseChat, isMobileLayout: false })}
        </ResizablePanel>
      ) : (
        renderMainColumn({ showChatButton: true })
      )}

      {isMobile && (
        <>
          <AskAIPill
            onOpen={() => updateSearchParam('chat', 'sheet')}
            hidden={mobileChatMode !== 'pill'}
          />
          <AskAISheet
            mode={mobileChatMode === 'pill' ? 'hidden' : mobileChatMode}
            onExpand={() => updateSearchParam('chat', 'expanded')}
            onCollapse={() => updateSearchParam('chat', 'sheet')}
            onClose={() => updateSearchParam('chat', null)}
            chatPanelSlot={
              <div className="relative w-full h-full">
                {renderChatColumn({
                  onCollapse: () => updateSearchParam('chat', null),
                  isMobileLayout: true,
                  hideHeader: true,
                  inputTrailingSlot: deployCtaSlot,
                })}
              </div>
            }
          />
        </>
      )}
    </div>
  )
}
